import { FormProvider, useForm, useWatch } from 'react-hook-form';
import ConfirmationDialog from '../../../components/messages/ConfirmationDialog';
import LoadingButton from '../../../components/inputs/LoadingButton';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import SLChangeOrdersSuccess from '../../../features/accounting/forms/SubcontractChangeOrders/SLChangeOrdersSuccess';
import ErrorToast, { showToast } from '../../../components/messages/ErrorAlert';
import { useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { useCompanyContext } from '../../../context/CompanyContext';
import SLChangeOrderInfo from '../../../features/accounting/forms/SubcontractChangeOrders/SLChangeOrderInfo';
import {
  SubcontractChangeOrderArgs,
  SubcontractCOFormFields,
} from '../../../Interfaces/Accounting/ACForms.interfaces';
import { useMutation } from '@tanstack/react-query';
import { postSLChangeOrders } from '../../../services/accounting/ACFormsService';
import { AxiosError } from 'axios';
import { checkItem } from '../../../features/accounting/forms/SubcontractChangeOrders/util';
import SLChangeOrderReport from '../../../features/accounting/forms/SubcontractChangeOrders/SLChangeOrderReport';
import SLChangeOrderReportHeader from '../../../features/accounting/forms/SubcontractChangeOrders/SLChangeOrderReportHeader';

const SubcontractChangeOrders = () => {
  const { selectedCompany } = useCompanyContext();
  const formMethods = useForm<SubcontractCOFormFields>();
  const { handleSubmit, setError, watch } = formMethods;
  const items = watch('items');
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const prevSelectedCompanyRef = useRef<string | null>(null);

  const { mutate, isLoading, isSuccess, data, reset } = useMutation({
    mutationFn: (args: SubcontractChangeOrderArgs) => {
      return postSLChangeOrders(selectedCompany!, { ...args });
    },
    onSuccess: () => {
      resetBackup();
    },
    onError: (error: AxiosError) => {
      const response = error.response;
      const data = response?.data as { message: string };
      showToast(
        'error',
        toast,
        'Upload Subcontract Change Order Error',
        data?.message || 'An error occurred! please try again later',
        10000
      );
    },
  });

  const onSubmit = (data: SubcontractCOFormFields) => {
    try {
      const items = data.items?.reduce(
        (ac, item) => {
          const { description, um, phase, units, unitCosts, retainage, total } =
            item;

          const error = checkItem(item);
          if (error) {
            throw new Error(error);
          }

          if (item.isNew) {
            ac.newItems?.push({
              item: item.item,
              description: description ?? '',
              um: um ?? '',
              phase: phase ?? '',
              retainage: (retainage ?? 0) / 100,
              ...(um === 'LS'
                ? { total: total ?? 0 }
                : { units: units ?? 0, unitCosts: unitCosts ?? 0 }),
            });
          } else if (item.isEdited) {
            ac.editItems?.push({
              item: item.item,
              ...(um === 'LS' ? { total: total ?? 0 } : { units: units ?? 0 }),
            });
          }

          return ac;
        },
        { newItems: [], editItems: [] } as Omit<
          SubcontractChangeOrderArgs,
          'sl'
        >
      );

      if (!items?.newItems?.length && !items?.editItems?.length) {
        throw new Error(
          'The form can only be submitted if an item was added or modified!'
        );
      }

      mutate({
        sl: data.sl,
        newItems: items?.newItems,
        editItems: items?.editItems,
      });
    } catch (e) {
      const errorMessage = (e as { message: string }).message;
      setError('items', { type: 'required' });
      showToast(
        'error',
        toast,
        'Upload Subcontract Change Order Error',
        errorMessage,
        10000
      );
    }
  };

  const resetBackup = () => {
    localStorage.removeItem('temp-sl-co-subcontract');
    localStorage.removeItem('temp-sl-co-items');
    localStorage.removeItem('temp-sl-co-items-original');
    localStorage.removeItem('temp-sl-co-company');
  };

  useEffect(() => {
    if (prevSelectedCompanyRef.current === null) {
      prevSelectedCompanyRef.current = selectedCompany?.id || null;
    } else if (prevSelectedCompanyRef.current !== selectedCompany?.id) {
      formMethods.reset();
      reset();
      resetBackup();
      prevSelectedCompanyRef.current = selectedCompany?.id || null;
    }
  }, [selectedCompany, formMethods, reset]);

  return (
    <div className="flex flex-column justify-content-center gap-5 pt-4">
      <div className="text-35px font-bold text-center mb-2 relative">
        Subcontract Change Orders
      </div>
      <FormProvider {...formMethods}>
        {!isSuccess && (
          <form className="mt-1 printBackground pdfDivFitContent flex flex-column gap-4">
            <div className="mt-3 px-3 flex w-full justify-content-between">
              <SLChangeOrderReport
                elementRef={tableRef}
                tableID="sl-co-items"
              />
            </div>
            <div ref={tableRef}>
              <SLChangeOrderReportHeader />
              <SLChangeOrderInfo />
            </div>
            <div className="flex flex-wrap flex-row-reverse gap-2 mb-3">
              <ConfirmationDialog
                Button={
                  <LoadingButton
                    isLoading={isLoading}
                    label="Submit"
                    className="w-15rem bluwaiBlue border-0 flex m-auto py-3"
                    fontSize="text-22px"
                    type="button"
                    disabled={!items?.length}
                  />
                }
                onConfirm={handleSubmit(onSubmit)}
                message="Are you sure you want to submit the subcontract change orders?"
              />
              <Button
                onClick={() => {
                  navigate('/accounting-dashboard/new');
                }}
                className="w-15rem bluwaiRed border-0 flex m-auto py-3"
                type="button"
              >
                <span className="mx-auto text-22px text-white">Cancel</span>
              </Button>
            </div>
          </form>
        )}
        {isSuccess && <SLChangeOrdersSuccess result={data} />}
      </FormProvider>
      <ErrorToast toastRef={toast} />
    </div>
  );
};

export default SubcontractChangeOrders;
